import type { AccountSummary, AuthState } from "@tg/shared";

// admin = овнер; geo_admin = админ с набором гео (видит только саппортов,
// аккаунты и шаблоны своих гео).
export type AppRole = "admin" | "geo_admin" | "user" | "support";

export interface GeoRecord {
  geoId: string;
  title: string;
}

/** Роль admin/geo_admin — управляющие (панель настроек, дашборды команды). */
export const isManagerRole = (role: AppRole | null | undefined): boolean =>
  role === "admin" || role === "geo_admin";

export interface AppAuthSession {
  authenticated: boolean;
  bootstrapRequired: boolean;
  user?: {
    userId: string;
    login: string;
    role: AppRole;
  };
}

export interface AppUserSummary {
  userId: string;
  login: string;
  role: AppRole;
  geo?: string | null;
  geoId?: string | null;
  /** Набор гео гео-админа (в списке юзеров отдаётся только овнеру). */
  geoIds?: string[];
  isBlocked?: boolean;
  blockedReason?: string;
  blockedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTeamMember {
  userId: string;
  login: string;
  geo: string | null;
}

export interface AppAuditEvent {
  eventId: string;
  action: string;
  actorUserId?: string;
  actorLogin?: string;
  targetUserId?: string;
  targetUserLogin?: string;
  targetAccountId?: string;
  details?: Record<string, unknown>;
  occurredAt: string;
}

export const httpBaseFromWsUrl = (wsUrl: string): string => {
  const normalized = wsUrl.replace(/^ws:/, "http:").replace(/^wss:/, "https:");
  return normalized.replace(/\/ws$/, "");
};

const request = async <TResponse>(
  url: string,
  init?: RequestInit
): Promise<TResponse> => {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Request failed with status ${response.status}`);
  }
  return (await response.json()) as TResponse;
};

export const authApi = {
  getSession(wsUrl: string): Promise<AppAuthSession> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AppAuthSession>(`${base}/api/app-auth/session`);
  },
  bootstrapUser(login: string, password: string, wsUrl: string): Promise<AppAuthSession> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AppAuthSession>(`${base}/api/app-auth/bootstrap`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ login, password })
    });
  },
  login(login: string, password: string, wsUrl: string): Promise<AppAuthSession> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AppAuthSession>(`${base}/api/app-auth/login`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ login, password })
    });
  },
  logout(wsUrl: string): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/app-auth/logout`, {
      method: "POST"
    });
  },
  createUser(
    login: string,
    password: string,
    role: AppRole,
    wsUrl: string,
    geoId?: string | null
  ): Promise<{ user: AppUserSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ user: AppUserSummary }>(`${base}/api/app-auth/users`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ login, password, role, ...(geoId ? { geoId } : {}) })
    });
  },
  listUsers(wsUrl: string): Promise<{ users: AppUserSummary[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ users: AppUserSummary[] }>(`${base}/api/app-auth/users`);
  },
  changeOwnPassword(
    currentPassword: string,
    newPassword: string,
    wsUrl: string
  ): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/app-auth/password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ currentPassword, newPassword })
    });
  },
  resetUserPassword(
    userId: string,
    newPassword: string,
    wsUrl: string
  ): Promise<{ user: AppUserSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ user: AppUserSummary }>(`${base}/api/app-auth/users/password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, newPassword })
    });
  },
  updateUserRole(
    userId: string,
    role: AppRole,
    wsUrl: string
  ): Promise<{ user: AppUserSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ user: AppUserSummary }>(`${base}/api/app-auth/users/role`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, role })
    });
  },
  updateUserGeo(
    userId: string,
    geo: string | null,
    wsUrl: string
  ): Promise<{ user: AppUserSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ user: AppUserSummary }>(`${base}/api/app-auth/users/geo`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, geo })
    });
  },
  // --- Справочник гео (роль geo_admin) ---
  listGeos(wsUrl: string): Promise<{ geos: GeoRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ geos: GeoRecord[] }>(`${base}/api/admin/geos`);
  },
  createGeo(title: string, wsUrl: string): Promise<{ geo: GeoRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ geo: GeoRecord }>(`${base}/api/admin/geos`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title })
    });
  },
  renameGeo(geoId: string, title: string, wsUrl: string): Promise<{ geo: GeoRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ geo: GeoRecord }>(`${base}/api/admin/geos/rename`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ geoId, title })
    });
  },
  updateUserGeoId(
    userId: string,
    geoId: string | null,
    wsUrl: string
  ): Promise<{ user: AppUserSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ user: AppUserSummary }>(`${base}/api/app-auth/users/geo-id`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, geoId })
    });
  },
  setUserGeoIds(
    userId: string,
    geoIds: string[],
    wsUrl: string
  ): Promise<{ geoIds: string[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ geoIds: string[] }>(`${base}/api/admin/users/geos`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, geoIds })
    });
  },
  setAccountGeo(
    accountId: string,
    geoId: string | null,
    wsUrl: string
  ): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/admin/accounts/geo`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, geoId })
    });
  },
  deleteGeo(geoId: string, wsUrl: string): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/admin/geos/delete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ geoId })
    });
  },
  deleteUser(userId: string, wsUrl: string): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/app-auth/users/delete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId })
    });
  },
  // Саппорты, доступные админу для просмотра смены: в пределах его гео и
  // (если передан accountId) с доступом к этому аккаунту.
  listSupportTeam(
    wsUrl: string,
    accountId?: string
  ): Promise<{ geo: string | null; supports: SupportTeamMember[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    const query = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return request<{ geo: string | null; supports: SupportTeamMember[] }>(
      `${base}/api/support/team${query}`
    );
  },
  listAuditEvents(wsUrl: string, limit = 50): Promise<{ events: AppAuditEvent[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ events: AppAuditEvent[] }>(
      `${base}/api/app-auth/audit?limit=${encodeURIComponent(String(limit))}`
    );
  },
  listAccounts(wsUrl: string): Promise<{ accounts: AccountSummary[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ accounts: AccountSummary[] }>(`${base}/api/accounts`);
  },
  bootstrapAccount(wsUrl: string, label?: string, geoId?: string): Promise<AccountSummary> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AccountSummary>(`${base}/api/accounts/bootstrap`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...(label ? { label } : {}), ...(geoId ? { geoId } : {}) })
    });
  },
  renameAccount(accountId: string, wsUrl: string, label: string): Promise<AccountSummary> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AccountSummary>(`${base}/api/accounts/rename`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, label })
    });
  },
  deleteAccount(accountId: string, wsUrl: string): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/accounts/delete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId })
    });
  },
  getState(accountId: string, wsUrl: string): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(
      `${base}/api/auth/state?accountId=${encodeURIComponent(accountId)}`
    );
  },
  start(accountId: string, wsUrl: string, restart = false): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(`${base}/api/auth/start`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, ...(restart ? { restart } : {}) })
    });
  },
  requestQrCode(accountId: string, wsUrl: string): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(`${base}/api/auth/qr`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId })
    });
  },
  submitPhoneNumber(accountId: string, wsUrl: string, phoneNumber: string): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(`${base}/api/auth/phone`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, phoneNumber })
    });
  },
  submitCode(accountId: string, wsUrl: string, code: string): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(`${base}/api/auth/code`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, code })
    });
  },
  submitPassword(accountId: string, wsUrl: string, password: string): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(`${base}/api/auth/password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, password })
    });
  },
  submitName(
    accountId: string,
    wsUrl: string,
    firstName: string,
    lastName?: string
  ): Promise<AuthState> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<AuthState>(`${base}/api/auth/name`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, firstName, ...(lastName ? { lastName } : {}) })
    });
  }
};
